
import { filter, take, tap, switchMap, shareReplay, map } from 'rxjs/operators';
import { APP_BASE_HREF } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { attributeTypesEnum } from '../../Misc/enums/attribute-types.enum';
import { AttributeModel } from '../../Misc/models/attribute.model';
import moment from 'moment-timezone';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { SharedReportApi } from '../../../api/reports/shared-report.api';
import { FilterTypeModel } from '../../Filter/models/abstract-filter-type.model';
import { FilterOptionModel } from '../../Filter/models/filter-option.model';
import { FilterTypeCheckboxModel } from '../../Filter/models/filter-type-checkbox.model';
import { FilterTypeTextModel } from '../../Filter/models/filter-type-text.model';
import { FilterService } from '../../Filter/services/abstract-filter.service';
import { DateQueryModel } from '../../Forms/models/date-query.model';
import { PageCriteriaModelInterface } from '../../Misc/models/page-criteria.model.interface';
import { PaginatorServiceInterface } from '../../Misc/services/paginator.service.interface';
import { SortOrderServiceInterface } from '../../Misc/services/sort-order.service.interface';
import { UsersService } from '../../../Core/Users/services/users.service';
import { LocalizedEnum } from '../../../api/journodex/model/localized-enum';
import { SharedReportDetailsEnum } from '../enums/shared-report-details.enum';
import { SharedReportFiltersEnum } from '../enums/shared-report-filters.enum';
import { SharedReportFilterModel } from '../models/shared-report-filter.model';
import { SharedReportListItemModel } from '../models/shared-report-list-item.model';
import { SharedReportModel } from '../models/shared-report.model';
import { ReportsService } from './reports.service';
import { WebsocketAwareNeptuneService, ResourceType } from '../../../Bootstrap/services/websocket-aware-neptune.service';
import { ReportModel } from '../models/report.model';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';

// TODO NEP-4792, for now, filter/sort features are handled on the front end
// but ideally it is better for the backend to work the same as mentions to include those functions (NEP-5666).
@Injectable()
export class SharedReportSearchService extends FilterService implements OnDestroy, PaginatorServiceInterface, SortOrderServiceInterface {

    protected subscriptions: Subscription[] = [];

    protected searchLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    protected nResults: number = 0;
    protected nResults$: BehaviorSubject<number> = new BehaviorSubject<number>(this.nResults);

    protected expirationCount: number = 0;
    protected expirationCount$: BehaviorSubject<number> = new BehaviorSubject<number>(this.expirationCount);

    protected results:  SharedReportListItemModel[] = [];
    protected results$: BehaviorSubject<SharedReportListItemModel[]> = new BehaviorSubject<SharedReportListItemModel[]>(this.results);

    protected displayResults:  SharedReportListItemModel[] = [];
    protected displayResults$: BehaviorSubject<SharedReportListItemModel[]> = new BehaviorSubject<SharedReportListItemModel[]>(this.displayResults);

    private focusedSharedReport:  SharedReportModel;
    private focusedSharedReport$: BehaviorSubject<SharedReportModel> = new BehaviorSubject<SharedReportModel>(null);

    protected dateQuery: DateQueryModel = DateQueryModel.getAllQuery();
    protected dateQuery$: BehaviorSubject<DateQueryModel> = new BehaviorSubject<DateQueryModel>(this.dateQuery);

    protected sortOrder: SortOrderEnum = SortOrderEnum.descending;
    protected sortOrder$: BehaviorSubject<SortOrderEnum> = new BehaviorSubject<SortOrderEnum>(this.sortOrder);

    protected sharedReportSortFields$: BehaviorSubject<LocalizedEnum[]> = new BehaviorSubject<LocalizedEnum[]>([]);

    protected sortField: LocalizedEnum = null;
    protected sortField$: BehaviorSubject<LocalizedEnum> = new BehaviorSubject<LocalizedEnum>(this.sortField);

    protected mainSearchRequest: Subscription;

    public reportSourceValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    private filtersLocked: boolean = false;
    private filtersLocked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.filtersLocked);
    private readonly lockedFiltersSessionKey: string = 'sharedReport-filters';

    cachedReport: ReportModel = null;

    private sharedReportSubject = new Subject<SharedReportModel>();
    private loadedReport$: Observable<ReportModel>;

    sortFields: LocalizedEnum[] = [
        {
            enumValue: 'CREATED_DATE',
            displayString: 'Reports.sharedReports.sortFields.createdDate'
        },
        {
            enumValue: 'NAME',
            displayString: 'Reports.sharedReports.sortFields.name'
        },
        {
            enumValue: 'CREATED_BY',
            displayString: 'Reports.sharedReports.sortFields.createdBy'
        }
    ];

    protected nameFilter: FilterTypeTextModel = new FilterTypeTextModel(
        SharedReportFiltersEnum.name,
        'Reports.sharedReports.filters.name',
        []
    );

    protected createdByFilter: FilterTypeCheckboxModel<string> = new FilterTypeCheckboxModel<string>(
        SharedReportFiltersEnum.createdBy,
        'Reports.sharedReports.filters.createdBy'
    );

    protected allFilters: FilterTypeModel[];

    page: number = 1;
    pageSize: number = 25;
    totalNumberOfPages: number = 1;
    pagingValidator: () => Observable<boolean>;

    protected pageCriteria: PageCriteriaModelInterface = {page: 1, pageSize: 25};
    protected pageCriteria$: BehaviorSubject<PageCriteriaModelInterface> = new BehaviorSubject<PageCriteriaModelInterface>(this.pageCriteria);

    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private sharedReportApi: SharedReportApi,
        private userService: UsersService,
        private reportsService: ReportsService,
        private websocketEvents: WebsocketAwareNeptuneService
    ) {
        super();

        this.loadedReport$ = this.sharedReportSubject.pipe(
            switchMap(sharedReport =>
                this.reportsService.getReportListItems().pipe(
                    switchMap(listItems => {
                        if (listItems.map(item => item.id).includes(sharedReport.reportId)) {
                            this.reportSourceValid$.next(true);
                            if (this.cachedReport && this.cachedReport.id === sharedReport.reportId) {
                                return of(this.cachedReport);
                            } else {
                                return this.reportsService.getReport(sharedReport.reportId).pipe(
                                    switchMap(report => this.reportsService.loadReportSavedSearches(report))
                                );
                            }
                        } else {
                            this.reportSourceValid$.next(false);
                            return of(null);
                        }
                    }),
                    tap(report => {
                        if (report && this.cachedReport?.id !== report.id) {
                            this.reportsService.setCurrentReport(report);
                            this.cachedReport = report.clone(false);
                        }
                    }),
                )
            ),
            shareReplay(1)
        );

        this.allFilters = this.getFilterDefinitions();
        this.setFilters(this.allFilters);

        this.loadLockedFiltersFromSession();

        this.sharedReportSortFields$.next(this.sortFields);
        this.sortField = this.sortFields[0];
        this.sortField$.next(this.sortField);

        this.pageCriteria.page = 1;
        this.setPageCriteria({page: 1, pageSize: 25}, false);

        this.loadListAndSearch();

        this.subscriptions.push(
            this.websocketEvents.getWebsocketMessages().pipe(
                filter(x => !!x))
                .subscribe(
                    message => {
                        if (
                            message.resourceType === ResourceType.sharedReports &&
                            message.slug &&
                            !(message.slug as string).includes('send')
                        ) {
                            this.loadListAndSearch();
                        }
                    }
                )
        );
    }

    doSearch(): void{
        this.clearSearch();
        this.handleFilter();
        this.handleSort();
        this.nResults = this.results.length;

        this.displayResults = this.results?.slice((this.page - 1) * this.pageSize, this.page * this.pageSize);
        if (this.displayResults?.length > 0) {
            this.setFocusedSharedReport(this.displayResults[0]);
        }
        this.setSearchResults();
    }

    /**
     * handle sort of shared reports
     */
    private handleSort(): void {
        // @HACK after the backend supports fetching sorted/filtered data, remove this
        switch (this.sortField.enumValue) {
            case this.sortFields[1].enumValue:
                // NAME
                if (this.sortOrder === SortOrderEnum.ascending) {
                    this.results.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
                } else {
                    this.results.sort((a, b) => b.name.toLocaleLowerCase().localeCompare(a.name.toLocaleLowerCase()));
                }
                break;
            case this.sortFields[2].enumValue:
                // CREATED_BY
                if (this.sortOrder === SortOrderEnum.ascending) {
                    this.results.sort((a, b) => a.createdBy.toLocaleLowerCase().localeCompare(b.createdBy.toLocaleLowerCase()));
                } else {
                    this.results.sort((a, b) => b.createdBy.toLocaleLowerCase().localeCompare(a.createdBy.toLocaleLowerCase()));
                }
                break;
            default:
                // CREATED_DATE
                if (this.sortOrder === SortOrderEnum.ascending) {
                    this.results.sort((a, b) => (a.createdDate > b.createdDate) ? 1 : -1);
                } else {
                    this.results.sort((a, b) => (a.createdDate < b.createdDate) ? 1 : -1);
                }
                break;
        }
    }

    /**
     * handle filter of shared reports
     */
    private handleFilter(): void {
        // @HACK after the backend supports fetching sorted/filtered data, remove this

        // handle date rage
        this.results = this.results.filter(listItem => {
            return DateQueryModel.isInDateQuery(listItem.createdDate, this.dateQuery);
        });

        // handle name
        const nameFilterValues = this.nameFilter.getValue();
        if (nameFilterValues?.length > 0) {
            this.results = this.results.filter(listItem => {
                let containKeyword = true;
                nameFilterValues.forEach(keyword => {
                    if (!listItem.name.toLowerCase().includes(keyword.toLowerCase())) {
                        containKeyword = false;
                    }
                });
                return containKeyword;
            });
        }

        // handle created by
        const createdByFilterValues = this.createdByFilter.getValue();
        if (createdByFilterValues?.length > 0) {
            this.results = this.results.filter(listItem => {
                return createdByFilterValues.indexOf(listItem.createdBy) !== -1;
            });
        }
    }

    setExpirationCount(): void {
        this.expirationCount = 0;
        const today = new Date();
        this.displayResults.map(report => {
            if ( moment(report.expiryDate).diff(today, 'days') <= 30) {
                this.expirationCount ++;
            }
        });
        this.expirationCount$.next(this.expirationCount);
    }

    getExpirationCount(): Observable<number> {
        return this.expirationCount$;
    }

    getSortableFields(): Observable<any[]> {
        return this.sharedReportSortFields$;
    }
    getSortOrder(): Observable<SortOrderEnum> {
        return this.sortOrder$;
    }
    getSortField(): Observable<any> {
        return this.sortField$;
    }
    setSortOrder(order: SortOrderEnum): void {
        if (this.sortOrder !== order) {
            this.sortOrder = order;
            this.sortOrder$.next(order);
            this.refreshResults();
        }
    }
    setSortField(field: any): void {
        if (field !== this.sortField) {
            this.sortField = field;
            this.sortField$.next(field);
            this.refreshResults();
        }
    }

    refreshResults(): void {
        this.loadListAndSearch();
    }

    //#region Paginator interface
    getItemCount(): Observable<number> {
        return this.nResults$;
    }

    getResults(): Observable<SharedReportListItemModel[]> {
        return this.results$;
    }

    getDisplayResults(): Observable<SharedReportListItemModel[]> {
        return this.displayResults$;
    }

    getFocusedSharedReport() {
        return this.focusedSharedReport$;
    }

    /**
     * Sets the focused shared report to a new value
     */
    public setFocusedSharedReport(sharedReport: SharedReportListItemModel) {
        this.focusedSharedReport = null;
        this.focusedSharedReport$.next(this.focusedSharedReport);
        this.sharedReportApi.get(sharedReport.id).subscribe(fullSharedReport => {
            this.focusedSharedReport = new SharedReportModel(fullSharedReport);
            this.focusedSharedReport$.next(this.focusedSharedReport);
        });
    }

    getSharedReportDetails(sharedReport: SharedReportModel): Observable<AttributeModel[]> {
        if (!sharedReport) {
            return of(null);
        }
        const name: AttributeModel = {
            id: SharedReportDetailsEnum.name,
            label: 'Reports.sharedReports.rhp.sharedReportName',
            value: sharedReport.name,
            type: attributeTypesEnum.simple
        };

        const title: AttributeModel = {
            id: SharedReportDetailsEnum.title,
            label: 'Reports.sharedReports.rhp.sharedReportTile',
            value: sharedReport.coverTitle,
            type: attributeTypesEnum.simple
        };

        const createdBy: AttributeModel = {
            id: SharedReportDetailsEnum.createdBy,
            label: 'createdBy',
            value: sharedReport.createdByUserId,
            type: attributeTypesEnum.user
        };

        const createdDate: AttributeModel = {
            id: SharedReportDetailsEnum.dateCreated,
            label: 'createdDate',
            value: sharedReport.createdDate,
            type: attributeTypesEnum.date,
        };

        const url: AttributeModel = {
            id: SharedReportDetailsEnum.url,
            label: 'Reports.sharedReports.rhp.url',
            // TODO: get url format from environment
            value: `${environment.sharedReportsUrl}/Report/${sharedReport.guid}`,
            type: attributeTypesEnum.url,
            // TODO: get url format from environment
            url: `${environment.sharedReportsUrl}/Report/${sharedReport.guid}`,
            showCopyIcon: true,
            message: {summary: 'Reports.sharedReports.rhp.growlMessage.urlCopiedSummary', detail: 'Reports.sharedReports.rhp.growlMessage.urlCopiedMessage'}
        };

        this.sharedReportSubject.next(sharedReport);
        return this.loadedReport$.pipe(
            take(1),
            map(report => {
                const sourceReport: AttributeModel = {
                    id: SharedReportDetailsEnum.sourceReport,
                    label: 'Reports.sharedReports.rhp.sourceReport',
                    value: report ? report.name : 'Reports.sharedReports.rhp.sourceReport.unknown',
                    type: report ? attributeTypesEnum.url : attributeTypesEnum.simple,
                    url: report ? this.appBaseHref + 'Report/reports/report/' + sharedReport.reportId : '',
                };
                return [name, title, createdBy, createdDate, sourceReport, url];
            }),
        );
    }

    getEditSharedReportDetails(sharedReport: SharedReportModel): Observable<AttributeModel[]> {
        if (!sharedReport) {
            return of(null);
        }
        const name: AttributeModel = {
            id: SharedReportDetailsEnum.name,
            label: 'Reports.sharedReports.rhp.sharedReportName',
            value: sharedReport.name,
            type: attributeTypesEnum.simple
        };

        const title: AttributeModel = {
            id: SharedReportDetailsEnum.title,
            label: 'Reports.sharedReports.rhp.sharedReportTile',
            value: sharedReport.coverTitle,
            type: attributeTypesEnum.simple
        };

        const createdBy: AttributeModel = {
            id: SharedReportDetailsEnum.createdBy,
            label: 'createdBy',
            value: sharedReport.createdByUserId,
            type: attributeTypesEnum.user
        };

        const createdDate: AttributeModel = {
            id: SharedReportDetailsEnum.dateCreated,
            label: 'createdDate',
            value: sharedReport.createdDate,
            type: attributeTypesEnum.date,
        };

        this.sharedReportSubject.next(sharedReport);
        return this.loadedReport$.pipe(
            take(1),
            map(() => [name, title, createdBy, createdDate] as AttributeModel[]),
        );
    }

    /**
     * Gets the current page criteria
     */
    getPageCriteria(): Observable<PageCriteriaModelInterface> {
        return this.pageCriteria$;
    }

    setMaxPageCriteria(totalNumberOfPages: number): void {
        this.totalNumberOfPages = totalNumberOfPages;
    }

    setPageCriteria(pageCriteria: PageCriteriaModelInterface,  search: boolean = true): void {
        this.page = pageCriteria.page;
        this.pageSize = pageCriteria.pageSize;
        this.nResults$.next(this.nResults);
        if (this.page <= this.totalNumberOfPages) {
            this.pageCriteria = pageCriteria;
            this.pageCriteria$.next(pageCriteria);
            if (search) {
                this.loadListAndSearch();
            }
        }
    }

    canSetPageCriteria(): Observable<boolean> {
        if (this.pagingValidator) {
            return this.pagingValidator();
        }
        return of(true);
    }

    /**
     * Checks that the page criteria can be updated
     * @NOTE This is an observable so that users can make the decision via dialogs etc.
     */
    public registerPagingValidator(validator: () => Observable<boolean>): void {
        this.pagingValidator = validator;
    }

    setSearchResults(): void {
        this.setExpirationCount();
        this.nResults$.next(this.nResults);
        this.displayResults$.next(this.displayResults);
        this.focusedSharedReport$.next(this.focusedSharedReport);
        this.expirationCount$.next(this.expirationCount);
        this.searchLoading$.next(false);
    }

    setDateQuery(dateQuery: DateQueryModel): void {
        this.dateQuery = dateQuery;
        this.dateQuery$.next(this.dateQuery);

        if (this.filtersLocked) {
            this.updateLockedFiltersSessionStorage();
        }
    }

    getDateQuery(): Observable<DateQueryModel> {
        return this.dateQuery$;
    }

    setFiltersLocked(locked: boolean): void {
        this.filtersLocked = locked;
        this.filtersLocked$.next(this.filtersLocked);

        this.updateLockedFiltersSessionStorage();
    }

    getFiltersLocked(): Observable<boolean> {
        return this.filtersLocked$;
    }

    filtersAreLocked(): boolean {
        return this.filtersLocked;
    }

    private updateLockedFiltersSessionStorage(): void {
        if (this.filtersLocked) {
            const filters = this.generateFilters();
            sessionStorage.setItem(this.lockedFiltersSessionKey, JSON.stringify(filters));
        } else {
            sessionStorage.removeItem(this.lockedFiltersSessionKey);
        }
    }

    private loadLockedFiltersFromSession(): void {
        const filtersRaw: string = sessionStorage.getItem(this.lockedFiltersSessionKey);
        if (filtersRaw) {
            try {
                const filters = new SharedReportFilterModel(JSON.parse(filtersRaw));
                this.populateFiltersFromSearch(filters);
                this.filtersLocked = true;
                this.filtersLocked$.next(this.filtersLocked);
                this.applyFilters();
            } catch (err) {
                this.clearFilters();
            }
        }
    }

    populateFiltersFromSearch(filters: SharedReportFilterModel): void {
        if (filters.dateQueryFilter) {
            this.setDateQuery(DateQueryModel.clone(filters.dateQueryFilter));
        }

        if (filters.nameFilterValues) {
            this.nameFilter.setValue(filters.nameFilterValues);
        }

        if (filters.createdByFilterValues) {
            this.createdByFilter.options = filters.createdByFilterOptions;
            this.createdByFilter.setValue(filters.createdByFilterValues);
        }
    }

    generateFilters(): SharedReportFilterModel {
        const filters = new SharedReportFilterModel();
        filters.dateQueryFilter = this.dateQuery;
        filters.nameFilterValues = this.nameFilter.getValue();
        filters.createdByFilterValues = this.createdByFilter.getValue();
        filters.createdByFilterOptions = this.createdByFilter.options;

        return filters;
    }

    /**
     * Applies the current filters onto the search
     */
    applyFilters(): void {
        super.applyFilters();
        this.setPageNumber(1);
        this.loadListAndSearch();
    }

    protected setFilters(filters: FilterTypeModel[]): void {
        super.setFilters(filters);

        if (this.filtersLocked) {
            this.updateLockedFiltersSessionStorage();
        }
    }

    setPageNumber(page: number): void {
        this.page = page;
        this.pageCriteria$.next({ page: this.page, pageSize: this.pageSize });
    }

    getFilterDefinitions(): FilterTypeModel[] {
        return  [
            this.nameFilter,
            this.createdByFilter
        ];
    }

    clearSearch(): void {
        this.displayResults = [];
        this.displayResults$.next(this.displayResults);
    }

    /**
     * fetch all the shared report list items from the backend, and then call doSearch() to apply filter/sort options
     */
    loadListAndSearch(): void {
        this.searchLoading$.next(true);
        if (this.mainSearchRequest) {
            this.mainSearchRequest.unsubscribe();
            this.mainSearchRequest = null;
        }

        this.mainSearchRequest = this.sharedReportApi.getList().pipe(
            tap(items => this.results = items.map(item => new SharedReportListItemModel(item))),
            // @HACK after the backend supports fetching sorted/filtered data, remove this
            switchMap(_ => this.userService.getUsers(this.results.map(item => item.createdByUserId)))
        ).subscribe(users => {
            const createdByOptions: FilterOptionModel<string>[] = [];
            users.forEach(user => {
                let count = 0;
                this.results.forEach(listItem => {
                    if (user.id === listItem.createdByUserId) {
                        listItem.createdBy = user.getDisplayName();
                        count++;
                    }
                });

                createdByOptions.push({
                    label: user.getDisplayName(),
                    value: user.getDisplayName(),
                    count: count
                } as FilterOptionModel<string>);
            });
            this.createdByFilter.options = createdByOptions;
            this.createdByFilter.options.sort((a, b) => {
                return b.count - a.count;
            });
            this.doSearch();
        });
    }

    getSearchLoading(): Observable<boolean> {
        return this.searchLoading$;
    }

    isTitleDuplicate(title: string, id?: number): boolean {
        return !!this.results.find(listItem => listItem.name.toLocaleLowerCase() === title.toLocaleLowerCase() && id !== listItem.id);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    resetCachedReport(): void {
        this.cachedReport = null;
    }
}
